import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { ArnContext, FormContext } from 'contexts';
import { ArnApplicationDvsMethodFormData } from 'types/forms/arnApplicationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { getIdentityDocumentTitleAndDataModel } from 'helpers/arnApplicationHelper';
import {
  ApplicationName,
  FormNavigation,
  ControlInputGroup
} from 'components/CASAForm';
import {
  AuCallout,
  AuFormItemWrapper,
  AuDirectionLink
} from 'components/DTA';

import { NAME_CHANGE_DOC_TYPES } from 'arnConstants';

interface PreviousNameProps {
  nextStep: string;
}

const PreviousName: React.FC<PreviousNameProps> = (props: PreviousNameProps) => {
  const { setPreviousStep, previousStep } = useContext(ArnContext);
  const { values, getFieldMeta, validateForm, setFieldValue } = useFormikContext<ArnApplicationDvsMethodFormData>();
  const { setFormAlert, openStepById, setOnChangeValidation, handleBack } = useContext(FormContext);

  const selectedIdentity = getIdentityDocumentTitleAndDataModel(values.identityType);
  const givenNames = getFieldMeta<string>(`${selectedIdentity.identityType}.givenNames`).value;
  const familyName = getFieldMeta<string>(`${selectedIdentity.identityType}.familyName`).value;
  const fullName = givenNames + ' ' + familyName;

  const handleNext = async () => {
    const validationErrors = await validateForm();

    if (!validationErrors || isEmpty(validationErrors)) {
      setOnChangeValidation(false);
      setFormAlert(null);
      if (values.previousName.nameChangeAgree === 'yes') {
        setFieldValue('firstName', givenNames);
        setFieldValue('lastName', familyName);
        setFieldValue('otherGivenNames', '');
      }
      const nextStepId = values.previousName.nameChangeAgree === 'yes' || values.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NONE ?
        'DeclarationAndSubmit' : props.nextStep;

      openStepById(nextStepId);
      setPreviousStep('PreviousName');
      return;
    }

    const errorAlert: IFormAlertType = {
      as: 'error',
      title: 'There was a problem submitting the form',
      message: 'Please check the form again before proceeding'
    };

    setFormAlert(errorAlert);
    setOnChangeValidation(true);
  };

  const handleBackAction = () => {
    if (previousStep && previousStep === 'PreviousName') {
      setPreviousStep('UploadFileForManualIdentification');
      openStepById('UploadFileForManualIdentification');
      return;
    }

    if (previousStep) {
      openStepById(previousStep);
      return;
    }

    handleBack();
  };

  return (
    <>
      <ApplicationName name='Previous name' />
      <AuDirectionLink
        onClick={handleBackAction}
        text="Back"
        direction="left" />
      <AuFormItemWrapper>
        <AuCallout >
          <p>
            The legal names you entered on your application and provided on your proof of identification do not match
          </p>
        </AuCallout>
      </AuFormItemWrapper>
      <AuFormItemWrapper>
        <p>Do you want to complete this application with your full legal name as <strong>{fullName}</strong> as it appears on your proof of identification?</p>
      </AuFormItemWrapper>
      <ControlInputGroup
        name='previousName.nameChangeAgree'
        type='radio'
        block
        options={[
          {
            label: 'Continue with your full name from proof of identification',
            value: 'yes',
            id: 'previousName-nameChangeAgree-yes'
          },
          {
            label: 'Provide a proof of name change document',
            value: 'no',
            id: 'previousName-nameChangeAgree-no'
          }
        ]}
      />
      {values.previousName?.nameChangeAgree === 'no' &&
        < ControlInputGroup
          name='previousName.changeOfNameDocument'
          label='Please select a change of name document'
          type='radio'
          block
          options={[
            {
              label: 'Change of name certificate',
              value: NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE,
              id: 'previousName-changeOfNameDocument-bc'
            },
            {
              label: 'Marriage Certificate',
              value: NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE,
              id: 'previousName-changeOfNameDocument-mc'
            },
            {
              label: 'Divorce papers',
              value: NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS,
              id: 'previousName-changeOfNameDocument-dp'
            },
            {
              label: 'I don\'t have any of these',
              value: NAME_CHANGE_DOC_TYPES.NONE,
              id: 'previousName-changeOfNameDocument-none-of-these'
            }
          ]}
        />
      }
      <FormNavigation
        nextButtonText='Next'
        onNextAction={handleNext}
        hidePrevious
        showSaveAndExit={false} />
    </>
  );
};

export default PreviousName;